import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import {
  SURGERY_STATUS,
  SURGERY_STATUS_LABELS,
  SURGERY_STATUS_COLORS,
  URGENCY,
  URGENCY_LABELS,
  URGENCY_COLORS,
} from '../shared/constants';

const useStyles = makeStyles((theme) => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  kpiRow: { marginBottom: 24 },
  kpiCard: {
    padding: 20,
    borderRadius: 8,
    textAlign: 'center',
    color: '#fff',
    minHeight: 100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  kpiValue: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 },
  kpiLabel: { fontSize: '0.8rem', marginTop: 4, opacity: 0.9 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  statusBar: {
    display: 'flex',
    borderRadius: 6,
    overflow: 'hidden',
    height: 28,
    marginBottom: 12,
  },
  statusSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: 28,
  },
  statusLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.8rem',
    color: '#555',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  completionHigh: { color: '#4CAF50', fontWeight: 600 },
  completionMed: { color: '#FF9800', fontWeight: 600 },
  completionLow: { color: '#F44336', fontWeight: 600 },
  urgencyCard: {
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    border: '2px solid',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  urgencyValue: { fontSize: '1.5rem', fontWeight: 700 },
  urgencyLabel: { fontSize: '0.8rem', marginTop: 4, color: '#555' },
}));

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const getMockKpis = (range) => {
  const multiplier = range === 'today' ? 1 : range === 'this_week' ? 5 : 22;
  return {
    total: 8 * multiplier,
    completed: 5 * multiplier,
    cancelled: 1 * multiplier,
    avgDurationMin: 94,
    emergencyRate: 12.5,
  };
};

const getMockStatusBreakdown = (total) => [
  { status: SURGERY_STATUS.COMPLETED, count: Math.round(total * 0.58) },
  { status: SURGERY_STATUS.PENDING, count: Math.round(total * 0.15) },
  { status: SURGERY_STATUS.STARTED, count: Math.round(total * 0.06) },
  { status: SURGERY_STATUS.PRE_OP_COMPLETE, count: Math.round(total * 0.08) },
  { status: SURGERY_STATUS.POST_OP, count: Math.round(total * 0.04) },
  { status: SURGERY_STATUS.CANCELLED, count: Math.round(total * 0.09) },
];

const DEPARTMENT_DATA = [
  { name: 'General Surgery', scheduled: 42, completed: 38, cancelled: 3 },
  { name: 'Orthopaedics', scheduled: 28, completed: 24, cancelled: 2 },
  { name: 'Obstetrics & Gynaecology', scheduled: 35, completed: 32, cancelled: 1 },
  { name: 'Urology', scheduled: 18, completed: 15, cancelled: 2 },
  { name: 'ENT', scheduled: 22, completed: 20, cancelled: 1 },
];

const URGENCY_COUNTS = [
  { urgency: URGENCY.ELECTIVE, count: 112 },
  { urgency: URGENCY.URGENT, count: 28 },
  { urgency: URGENCY.EMERGENCY, count: 16 },
];

// ── Component ────────────────────────────────────────────────────────────────
const SurgeryDashboardStats = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const kpis = getMockKpis(dateRange);
  const statusBreakdown = getMockStatusBreakdown(kpis.total);
  const statusTotal = statusBreakdown.reduce((s, b) => s + b.count, 0);

  const completionClass = (rate) =>
    rate >= 80 ? classes.completionHigh : rate >= 60 ? classes.completionMed : classes.completionLow;

  return (
    <div>
      <PageTitle title="Surgery Reports" />
      <SubHeader title="Surgery" subTitle="Surgery Reports" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Date Range Filter ─────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </div>

        {/* ── KPI Cards ─────────────────────────────────────────────── */}
        <Grid container spacing={2} className={classes.kpiRow}>
          {[
            { label: 'Total Surgeries', value: kpis.total, color: '#3F51B5' },
            { label: 'Completed', value: kpis.completed, color: '#009688' },
            { label: 'Cancelled', value: kpis.cancelled, color: '#F44336' },
            { label: 'Avg Duration (min)', value: kpis.avgDurationMin, color: '#FF9800' },
            { label: 'Emergency Rate', value: `${kpis.emergencyRate}%`, color: '#E91E63' },
          ].map((k) => (
            <Grid item xs={12} sm={6} md key={k.label}>
              <Paper className={classes.kpiCard} style={{ backgroundColor: k.color }} elevation={2}>
                <Typography className={classes.kpiValue}>{k.value}</Typography>
                <Typography className={classes.kpiLabel}>{k.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Status Breakdown ──────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Surgeries by Status</Typography>
        <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
          <div className={classes.statusBar}>
            {statusBreakdown.map((s) => {
              const pct = statusTotal > 0 ? (s.count / statusTotal) * 100 : 0;
              if (pct === 0) return null;
              return (
                <div
                  key={s.status}
                  className={classes.statusSegment}
                  style={{
                    backgroundColor: SURGERY_STATUS_COLORS[s.status],
                    width: `${pct}%`,
                  }}
                >
                  {pct > 6 ? s.count : ''}
                </div>
              );
            })}
          </div>
          <div className={classes.statusLegend}>
            {statusBreakdown.map((s) => (
              <div key={s.status} className={classes.legendItem}>
                <span
                  className={classes.legendDot}
                  style={{ backgroundColor: SURGERY_STATUS_COLORS[s.status] }}
                />
                {SURGERY_STATUS_LABELS[s.status]}: {s.count}
              </div>
            ))}
          </div>
        </Paper>

        {/* ── Department Table ──────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Surgeries by Department</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell align="center">Scheduled</TableCell>
                <TableCell align="center">Completed</TableCell>
                <TableCell align="center">Cancelled</TableCell>
                <TableCell align="center">Completion Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEPARTMENT_DATA.map((dept) => {
                const rate = dept.scheduled > 0
                  ? Math.round((dept.completed / dept.scheduled) * 100)
                  : 0;
                return (
                  <TableRow key={dept.name}>
                    <TableCell className={classes.tableCell}>{dept.name}</TableCell>
                    <TableCell align="center" className={classes.tableCell}>{dept.scheduled}</TableCell>
                    <TableCell align="center" className={classes.tableCell}>{dept.completed}</TableCell>
                    <TableCell align="center" className={classes.tableCell}>{dept.cancelled}</TableCell>
                    <TableCell align="center" className={completionClass(rate)}>
                      {rate}%
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell className={classes.tableCell} style={{ fontWeight: 600 }}>Total</TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  {DEPARTMENT_DATA.reduce((s, d) => s + d.scheduled, 0)}
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  {DEPARTMENT_DATA.reduce((s, d) => s + d.completed, 0)}
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  {DEPARTMENT_DATA.reduce((s, d) => s + d.cancelled, 0)}
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  {Math.round(
                    (DEPARTMENT_DATA.reduce((s, d) => s + d.completed, 0) /
                      DEPARTMENT_DATA.reduce((s, d) => s + d.scheduled, 0)) * 100
                  )}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Urgency Distribution ──────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Urgency Distribution</Typography>
        <Grid container spacing={2}>
          {URGENCY_COUNTS.map((u) => (
            <Grid item xs={12} sm={4} key={u.urgency}>
              <Paper
                className={classes.urgencyCard}
                elevation={0}
                style={{ borderColor: URGENCY_COLORS[u.urgency] }}
              >
                <Typography
                  className={classes.urgencyValue}
                  style={{ color: URGENCY_COLORS[u.urgency] }}
                >
                  {u.count}
                </Typography>
                <Typography className={classes.urgencyLabel}>
                  {URGENCY_LABELS[u.urgency]}
                </Typography>
                <Chip
                  label={`${Math.round((u.count / URGENCY_COUNTS.reduce((s, x) => s + x.count, 0)) * 100)}%`}
                  size="small"
                  style={{
                    marginTop: 8,
                    alignSelf: 'center',
                    backgroundColor: URGENCY_COLORS[u.urgency],
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </div>
  );
};

export default SurgeryDashboardStats;
